/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Coins, Zap, Lock, Database, Shield, Brain } from 'lucide-react';
import { FEE_SCHEDULE } from '../data/fees';

interface FeesProps {
    // onBack prop is no longer needed
}

export const Fees: React.FC<FeesProps> = () => {
    return (
        <div className="min-h-screen bg-[#F9F8F4] flex flex-col relative animate-fade-in">
            <main className="flex-1 container mx-auto px-6 py-12 max-w-5xl">
                
                {/* Hero Header Card */}
                <div className="bg-stone-900 rounded-3xl p-8 md:p-12 mb-12 text-center relative overflow-hidden shadow-2xl">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-nobel-gold/10 rounded-full blur-[80px] pointer-events-none"></div>
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/10 rounded-full blur-[80px] pointer-events-none"></div>
                    
                    <div className="relative z-10">
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-stone-800 border border-stone-700 rounded-full mb-6">
                            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                            <span className="text-[10px] font-bold text-stone-300 uppercase tracking-widest">Protocol V2 Active</span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl text-white mb-6 leading-tight">
                            Transparent Pricing.<br/>
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-nobel-gold to-amber-600">Performance First.</span>
                        </h2>
                        <p className="text-stone-400 max-w-2xl mx-auto text-lg leading-relaxed">
                            We operate on a transparent, flat-fee model for deployment. 
                            Trading fees are competitive and partially redistributed to the community rewards pool.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                    
                    {/* Minting Fees */}
                    <div className="bg-white p-8 rounded-2xl border border-stone-200 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-2.5 bg-stone-100 rounded-xl text-stone-600">
                                <Database size={24} />
                            </div>
                            <h2 className="font-serif text-2xl font-bold text-stone-900">Token Launch</h2>
                        </div>
                        
                        <div className="space-y-6">
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <img src="https://cryptologos.cc/logos/solana-sol-logo.png" className="w-5 h-5" alt="SOL" />
                                    <h3 className="font-bold text-stone-700">Solana Deployment</h3>
                                </div>
                                <div className="bg-stone-50 p-4 rounded-xl space-y-3 border border-stone-100">
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-stone-600">Standard SPL</span>
                                        <span className="font-mono font-bold text-stone-900">{FEE_SCHEDULE.MINT.SOL.BASE} SOL</span>
                                    </div>
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-stone-600">Token-2022 Extensions</span>
                                        <span className="font-mono font-bold text-stone-900">+{FEE_SCHEDULE.MINT.SOL.TOKEN_2022_EXTRA} SOL</span>
                                    </div>
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-stone-600">Metadata Storage (Socials)</span>
                                        <span className="font-mono font-bold text-stone-900 text-xs">~0.002 SOL</span>
                                    </div>
                                    <div className="text-[10px] text-stone-400 pt-2 border-t border-stone-200 mt-2">
                                        Includes network rent exemption and service fee. Storage rent is dynamic based on description and link length.
                                    </div>
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <img src="https://cryptologos.cc/logos/sui-sui-logo.png" className="w-5 h-5" alt="SUI" />
                                    <h3 className="font-bold text-stone-700">Sui Deployment</h3>
                                </div>
                                <div className="bg-stone-50 p-4 rounded-xl space-y-3 border border-stone-100">
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-stone-600">Move Coin Package</span>
                                        <span className="font-mono font-bold text-stone-900">{FEE_SCHEDULE.MINT.SUI.BASE} SUI</span>
                                    </div>
                                    <div className="text-[10px] text-stone-400 pt-2 border-t border-stone-200 mt-2">
                                        Covers Move package publication and storage rebates.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Trading & Liquidity */}
                    <div className="space-y-8">
                        {/* Trading */}
                        <div className="bg-white p-8 rounded-2xl border border-stone-200 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="p-2.5 bg-stone-100 rounded-xl text-stone-600">
                                    <Zap size={24} />
                                </div>
                                <h2 className="font-serif text-2xl font-bold text-stone-900">Trading</h2>
                            </div>
                            <div className="space-y-3">
                                <div className="flex justify-between items-center p-3 bg-stone-50 rounded-lg">
                                    <span className="text-stone-600 font-medium">Swap Fee</span>
                                    <span className="font-mono font-bold text-stone-900">0.25%</span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-stone-50 rounded-lg">
                                    <span className="text-stone-600 font-medium">Network Fee (Solana)</span>
                                    <span className="font-mono font-bold text-stone-900">~0.000005 SOL</span>
                                </div>
                                <p className="text-xs text-stone-500 mt-2">
                                    Trading fees are applied to the output amount. A portion is routed to the Public Rewards Pool.
                                </p>
                            </div>
                        </div>

                        {/* Liquidity */}
                        <div className="bg-white p-8 rounded-2xl border border-stone-200 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="p-2.5 bg-stone-100 rounded-xl text-stone-600">
                                    <Lock size={24} />
                                </div>
                                <h2 className="font-serif text-2xl font-bold text-stone-900">Liquidity Management</h2>
                            </div>
                            <div className="space-y-3">
                                <div className="flex justify-between items-center p-3 bg-stone-50 rounded-lg">
                                    <span className="text-stone-600 font-medium">Lock LP (Solana)</span>
                                    <span className="font-mono font-bold text-stone-900">{FEE_SCHEDULE.LOCK.SERVICE_FEE_SOL} SOL</span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-stone-50 rounded-lg">
                                    <span className="text-stone-600 font-medium">Lock LP (Sui)</span>
                                    <span className="font-mono font-bold text-stone-900">{FEE_SCHEDULE.LOCK.SERVICE_FEE_SUI} SUI</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* AI Intelligence Pricing */}
                <div className="bg-gradient-to-br from-stone-900 to-stone-800 text-white rounded-3xl p-8 md:p-10 shadow-2xl relative overflow-hidden mb-12">
                    <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                        <div className="max-w-md">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-nobel-gold rounded-lg text-stone-900">
                                    <Brain size={24} />
                                </div>
                                <h2 className="font-serif text-3xl font-bold">Intelligence Access</h2>
                            </div>
                            <p className="text-stone-400 text-lg leading-relaxed mb-6">
                                Unlock the full power of the Neural Engine. Get real-time whale tracking, predictive analytics, and automated trading strategies.
                            </p>
                            <div className="flex gap-3">
                                <span className="px-3 py-1 bg-white/10 rounded-full text-xs font-bold uppercase tracking-wider">Swarm V2.2</span>
                                <span className="px-3 py-1 bg-white/10 rounded-full text-xs font-bold uppercase tracking-wider">Auto-Trade</span>
                            </div>
                        </div>

                        <div className="flex gap-4 w-full md:w-auto">
                            <div className="bg-white/5 border border-white/10 p-6 rounded-2xl flex-1 md:w-64 text-center hover:bg-white/10 transition-colors cursor-default">
                                <div className="text-sm font-bold text-stone-400 uppercase mb-2">Standard</div>
                                <div className="text-3xl font-serif font-bold mb-4">Free</div>
                                <ul className="text-xs text-stone-400 space-y-2 mb-6 text-left pl-4">
                                    <li>• Basic Market Signals</li>
                                    <li>• Public Leaderboard</li>
                                    <li>• Standard Execution</li>
                                </ul>
                                <button className="w-full py-2 bg-stone-700 rounded-lg text-xs font-bold hover:bg-stone-600 transition-colors">Current Plan</button>
                            </div>
                            <div className="bg-gradient-to-b from-nobel-gold to-amber-600 p-0.5 rounded-2xl flex-1 md:w-64 relative">
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-nobel-gold text-stone-900 px-3 py-0.5 rounded-full text-[10px] font-bold uppercase shadow-sm">
                                    Most Popular
                                </div>
                                <div className="bg-stone-900 h-full w-full rounded-[14px] p-6 text-center flex flex-col">
                                    <div className="text-sm font-bold text-nobel-gold uppercase mb-2">Pro Trader</div>
                                    <div className="text-3xl font-serif font-bold mb-4">0.5 SOL<span className="text-xs font-sans font-normal text-stone-500">/mo</span></div>
                                    <ul className="text-xs text-stone-300 space-y-2 mb-6 text-left pl-4">
                                        <li>• Real-Time Swarm Consensus</li>
                                        <li>• Smart Money Wallet Tracker</li>
                                        <li>• Auto-Trading Bot Access</li>
                                    </ul>
                                    <button className="w-full py-2 bg-nobel-gold text-stone-900 rounded-lg text-xs font-bold hover:bg-white transition-colors mt-auto">Upgrade</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Safeguards Footer */}
                <div className="text-center">
                    <p className="text-xs text-stone-500 uppercase tracking-widest font-bold mb-4">Platform Safeguards</p>
                    <div className="flex flex-wrap justify-center gap-6 text-sm text-stone-600">
                        <span className="flex items-center gap-2"><Shield size={16} /> Rate Limiting Active</span>
                        <span className="flex items-center gap-2"><Lock size={16} /> Bot Protection</span>
                        <span className="flex items-center gap-2"><Database size={16} /> Decentralized Storage</span>
                    </div>
                </div>

            </main>
        </div>
    );
};